import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { CampaignSummaryField } from '@fmnts/api/next';
import { isNotNull } from '@fmnts/core';
import {
  AbstractDeclarativeFieldComponent,
  TemplateBuilder,
  TemplatePart,
  ensureTemplate,
  interpolateTemplate,
} from '@fmnts/declarative-forms';
import { TranslationService } from '@fmnts/i18n';
import {
  BehaviorSubject,
  MonoTypeOperatorFunction,
  catchError,
  filter,
  map,
  of,
} from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';

/**
 * @returns
 * An `Observable` that emits a HTML formatted version of the
 * emitted string.
 */
function formatHtml(): MonoTypeOperatorFunction<string> {
  // Replace new lines with <br> tags, but trim before to avoid empty lines
  // for combined fields with semi-optional values (like phone numbers).
  const newlineRegex = /\n/g;
  const lineBreakTag = '<br>';

  return map((value) =>
    (value || '').trim().replace(newlineRegex, lineBreakTag),
  );
}

/**
 * Component that renders a summary for the form.
 *
 * This could be a templated string, which is specified by
 * setting `field.value` to a (translated) templated string like
 * - `field-key` -> which interpolates to the value of `field-key`
 * - a more elaborate template like `{{first_name}} {{last_name}}`
 *    which would resolve the given fields
 */
@Component({
  selector: 'next-text-summary-field',
  templateUrl: './text-summary-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule],
})
export class TextSummaryFieldComponent
  extends AbstractDeclarativeFieldComponent<CampaignSummaryField>
  implements OnInit
{
  protected readonly translationService = inject(TranslationService);
  protected readonly templateBuilder = inject(TemplateBuilder);

  private fieldValueSubject = new BehaviorSubject<string | null>(null);

  /**
   * Observable that emits with the field value
   */
  protected fieldValue$ = this.fieldValueSubject.pipe(filter(isNotNull));

  /**
   * `Observable` that emits with the interpolated value
   * for this fields templated string.
   */
  protected dynamicValue$ = this.translationService
    .fromInlineTranslation$(this.fieldValue$)
    .pipe(
      // Ensure that for any emitted value that references a field
      // that it is transformed into templating form of `{{some-field-key}}`
      map((s) => ensureTemplate(s, this.declaration)),
      this.transformTemplate(),
      interpolateTemplate((part) =>
        this.templateBuilder
          .interpolate(part)
          .pipe(this.transformInterpolated(part)),
      ),
    );

  /**
   * Observable that emits with the content formatted for HTML
   */
  public htmlContent$ = this.dynamicValue$.pipe(formatHtml());

  ngOnInit() {
    if (this.field) {
      const { defaultValue } = this.field.widgetOptions;

      this.fieldValueSubject.next(defaultValue);
    }
  }

  /**
   * Operator that provides a transformation for any emitted
   * templated string.
   *
   * @returns
   * An `Observable` that emits with the transformed value.
   */
  protected transformTemplate(): MonoTypeOperatorFunction<string> {
    // No transformation needed for the base component but we allow
    // any subclasses to provide their own transformation logic
    return (obs$) => obs$;
  }

  /**
   * Operator that provides a transformation for any emitted
   * interpolated template value.
   *
   * @param part Interpolated template part
   *
   * @returns
   * An `Observable` that emits a projection of the given
   * interpolated template `part`.
   */
  protected transformInterpolated(
    part: TemplatePart,
  ): MonoTypeOperatorFunction<string> {
    return catchError((e) => of(''));
  }
}
